import { CalendarDaysIcon, ChatBubbleLeftIcon, ClipboardDocumentCheckIcon, FlagIcon, HeartIcon, RectangleGroupIcon, SignalIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import { initEngagementI18next } from "@domains/engagement/client/i18n/i18next.ts";
import { initFeedbackI18next } from "@domains/feedback/client/i18n/i18next.ts";
import { initMeetingsI18next } from "@domains/meetings/client/i18n/i18next.ts";
import { isMeetingsInPerformanceEnabled } from "@domains/meetings/client/utils/meetingsInPerformanceAccess.ts";
import { REVIEW_GOOD_VIBE_SEGMENT } from "@domains/reviews/client/components/reviewRoutes.ts";
import { initSegmentsI18next } from "@domains/segments/client/i18n/i18next.ts";
import { initScopeI18next } from "@packages/core-client/scope/i18n/i18next.ts";
import type { AppDeferredRegistrationData, AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerArea } from "@packages/core-client/registration/registerArea.tsx";
import type { FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";
import { TEAM_ACCESS } from "#shared/access.ts";
import { TeamTabsHeading } from "./components/TeamTabsHeading.tsx";
import { TeamsLegacyRedirect } from "./TeamsLegacyRedirect.tsx";
import { initI18next } from "./i18n/i18next.ts";

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const requestHandlers = (await import("./api/mocks/getRequestHandlers.ts")).getRequestHandlers();

        runtime.registerRequestHandlers(requestHandlers);
    }
}

function registerReviewDetailRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewDetailArea = registerArea(runtime, "team-reviews-detail", "/team/:revieweeId/reviews/:reviewCycleId", ClipboardDocumentCheckIcon, i18nextInstance, { access: TEAM_ACCESS });

    reviewDetailArea.registerPage({
        page: "TeamReviewDetail",
        lazy: async () => {
            const { ReviewDetailPage } = await import("./pages/reviews/detail/ReviewDetailPage.tsx");
            const { ReviewDetailErrorBoundary } = await import("./pages/reviews/detail/ReviewDetailErrorBoundary.tsx");

            return { element: <ReviewDetailPage />, errorElement: <ReviewDetailErrorBoundary /> };
        }
    });
}

function registerReviewShareRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewShareArea = registerArea(runtime, "team-reviews-share", "/team/:revieweeId/reviews/:reviewCycleId/share", ClipboardDocumentCheckIcon, i18nextInstance, { access: TEAM_ACCESS });

    reviewShareArea.registerPage({
        page: "TeamReviewShare",
        lazy: async () => {
            const { TeamReviewSharePage } = await import("./pages/reviews/share/TeamReviewSharePage.tsx");
            const { ReviewDetailErrorBoundary } = await import("./pages/reviews/detail/ReviewDetailErrorBoundary.tsx");

            return { element: <TeamReviewSharePage />, errorElement: <ReviewDetailErrorBoundary /> };
        }
    });
}

function registerReviewWriteRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const reviewWriteArea = registerArea(runtime, "team-reviews-write", "/team/:revieweeId/reviews/:reviewCycleId/:perspective", ClipboardDocumentCheckIcon, i18nextInstance, { access: TEAM_ACCESS });

    reviewWriteArea.registerPage({
        page: "TeamReviewWrite",
        fullHeight: true,
        lazy: async () => {
            const { TeamReviewWritePage } = await import("./pages/reviews/write/TeamReviewWritePage.tsx");
            const { ReviewDetailErrorBoundary } = await import("./pages/reviews/detail/ReviewDetailErrorBoundary.tsx");

            return { element: <TeamReviewWritePage />, errorElement: <ReviewDetailErrorBoundary /> };
        }
    });

    reviewWriteArea.registerOverlay("good-vibe-detail", {
        segment: `${REVIEW_GOOD_VIBE_SEGMENT}/:recognitionId`,
        page: "TeamReviewWriteGoodVibeDetail",
        lazy: async () => {
            const { ReviewWriteGoodVibeRoute } = await import("./pages/reviews/write/ReviewWriteGoodVibeRoute.tsx");

            return { element: <ReviewWriteGoodVibeRoute /> };
        }
    });
}

async function loadTeamGoalProgressUpdateRoute() {
    const { TeamGoalProgressUpdateRoute } = await import("./pages/goals/detail/TeamGoalProgressUpdateRoute.tsx");

    return { element: <TeamGoalProgressUpdateRoute /> };
}

function registerGoalDetailRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalDetailArea = registerArea(runtime, "team-goals-detail", "/team/goals/:goalId", FlagIcon, i18nextInstance, { access: TEAM_ACCESS });

    goalDetailArea.registerPage({
        page: "TeamGoalDetail",
        lazy: async () => {
            const { TeamGoalDetailPage } = await import("./pages/goals/detail/TeamGoalDetailPage.tsx");
            const { TeamGoalDetailErrorBoundary } = await import("./pages/goals/detail/TeamGoalDetailErrorBoundary.tsx");

            return { element: <TeamGoalDetailPage />, errorElement: <TeamGoalDetailErrorBoundary /> };
        }
    });

    goalDetailArea.registerOverlay("progress-update", { segment: "update", page: "TeamGoalProgressUpdate", lazy: loadTeamGoalProgressUpdateRoute });
    goalDetailArea.registerOverlay("progress-update-edit", { segment: "update/:updateId", page: "TeamGoalProgressUpdate", lazy: loadTeamGoalProgressUpdateRoute });
}

function registerGoalEditRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const goalEditArea = registerArea(runtime, "team-goals-edit", "/team/goals/:goalId/edit", FlagIcon, i18nextInstance, { access: TEAM_ACCESS });

    goalEditArea.registerPage({
        page: "TeamGoalEdit",
        fullHeight: true,
        lazy: async () => {
            const { TeamGoalEditPage } = await import("./pages/goals/edit/TeamGoalEditPage.tsx");
            const { TeamGoalDetailErrorBoundary } = await import("./pages/goals/detail/TeamGoalDetailErrorBoundary.tsx");

            return { element: <TeamGoalEditPage />, errorElement: <TeamGoalDetailErrorBoundary /> };
        }
    });
}

function registerEngagementDetailRoutes(runtime: FireflyRuntime, i18nextInstance: i18n) {
    const engagementDetailArea = registerArea(runtime, "team-engagement-detail", "/team/engagement/:metricId", SignalIcon, i18nextInstance, { access: TEAM_ACCESS });

    engagementDetailArea.registerPage({
        page: "TeamEngagementMetricDetail",
        lazy: async () => {
            const { EngagementMetricDetailPage } = await import("./pages/engagement/detail/EngagementMetricDetailPage.tsx");
            const { EngagementMetricDetailErrorBoundary } = await import("./pages/engagement/detail/EngagementMetricDetailErrorBoundary.tsx");

            return { element: <EngagementMetricDetailPage />, errorElement: <EngagementMetricDetailErrorBoundary /> };
        }
    });
}

export const registerTeam: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initSegmentsI18next(runtime);
    initScopeI18next(runtime);
    initEngagementI18next(runtime);
    initMeetingsI18next(runtime);
    initFeedbackI18next(runtime);

    await registerMsw(runtime);

    const area = registerArea(runtime, "team", "/team", UserGroupIcon, i18nextInstance, { access: TEAM_ACCESS });

    runtime.registerRoute({ path: "/teams/*", element: <TeamsLegacyRedirect /> });

    const page = area.registerTabsArea("main", { preserveSearchParams: ["team-scope", "team-segment"], Heading: TeamTabsHeading });

    const overview = page.registerRoute("overview", {
        index: true,
        page: "TeamOverview",
        lazy: async () => {
            const { TeamOverviewPage } = await import("./pages/overview/TeamOverviewPage.tsx");

            return { element: <TeamOverviewPage /> };
        }
    });

    const engagement = page.registerRoute("engagement", {
        path: "/engagement",
        page: "TeamEngagement",
        lazy: async () => {
            const { TeamEngagementPage } = await import("./pages/engagement/TeamEngagementPage.tsx");

            return { element: <TeamEngagementPage /> };
        }
    });

    const meetings = page.registerRoute("meetings", {
        path: "/meetings",
        page: "TeamMeetings",
        lazy: async () => {
            const { TeamMeetingsPage } = await import("./pages/meetings/TeamMeetingsPage.tsx");

            return { element: <TeamMeetingsPage /> };
        }
    });

    const goodVibes = page.registerRoute("good-vibes", {
        path: "/good-vibes",
        page: "TeamGoodVibes",
        lazy: async () => {
            const { TeamGoodVibesPage } = await import("./pages/good-vibes/TeamGoodVibesPage.tsx");

            return { element: <TeamGoodVibesPage /> };
        }
    });

    goodVibes.registerOverlay("detail", {
        segment: ":recognitionId",
        page: "TeamGoodVibeDetail",
        lazy: async () => {
            const { GoodVibeDetailRoute } = await import("./pages/good-vibes/GoodVibeDetailRoute.tsx");

            return { element: <GoodVibeDetailRoute /> };
        }
    });

    const feedback = page.registerRoute("feedback", {
        path: "/feedback",
        page: "TeamFeedback",
        lazy: async () => {
            const { TeamFeedbackPage } = await import("./pages/feedback/TeamFeedbackPage.tsx");

            return { element: <TeamFeedbackPage /> };
        }
    });

    runtime.registerRoute({ path: "/team/feedback/:feedbackId", element: null }, { parentPath: "/team/feedback" });

    const reviews = page.registerRoute("reviews", {
        path: "/reviews",
        page: "TeamReviews",
        lazy: async () => {
            const { TeamReviewsPage } = await import("./pages/reviews/list/TeamReviewsPage.tsx");

            return { element: <TeamReviewsPage /> };
        }
    });

    const goals = page.registerRoute("goals", {
        path: "/goals",
        page: "TeamGoals",
        lazy: async () => {
            const { TeamGoalsPage } = await import("./pages/goals/list/TeamGoalsPage.tsx");

            return { element: <TeamGoalsPage /> };
        }
    });

    goals.registerOverlay("progress-update", { segment: "update/:goalId", page: "TeamGoalsProgressUpdate", lazy: loadTeamGoalProgressUpdateRoute });

    registerReviewDetailRoutes(runtime, i18nextInstance);
    registerReviewShareRoutes(runtime, i18nextInstance);
    registerReviewWriteRoutes(runtime, i18nextInstance);
    registerGoalDetailRoutes(runtime, i18nextInstance);
    registerGoalEditRoutes(runtime, i18nextInstance);
    registerEngagementDetailRoutes(runtime, i18nextInstance);

    return (deferredRuntime: FireflyRuntime, { session }: AppDeferredRegistrationData) => {
        if (!hasAnyRole(session, TEAM_ACCESS)) {
            return;
        }

        overview.registerLink({ priority: 1000, icon: RectangleGroupIcon });
        engagement.registerLink({ priority: 999, icon: SignalIcon });
        goodVibes.registerLink({ priority: 997, icon: HeartIcon });
        feedback.registerLink({ priority: 996, icon: ChatBubbleLeftIcon });
        reviews.registerLink({ priority: 995, icon: ClipboardDocumentCheckIcon });
        goals.registerLink({ priority: 994, icon: FlagIcon });

        if (isMeetingsInPerformanceEnabled(deferredRuntime.getFeatureFlag("meetings-in-performance", false), session)) {
            meetings.registerLink({ priority: 993, icon: CalendarDaysIcon });
        }

        area.registerToTopNav({ priority: 200 });
    };
};
